import { IYearMonth } from './date';
import { IBusiness } from './entities/businessBase';
import { IAnnounce } from './entities/forecast';
import { RegisterExcelInfo } from './entities/inputExcelInfo';
import { ISalesResult } from './entities/salesResults';
import { IUnmanagedBusiness } from './entities/unmanagedBusiness';

/**
 * IBusinessをラップしたエクセル転記用の型
 */
export type TranslateExcelBusiness = IBusiness & {
  translateBusinessId: string; // 転記のみに用いるID
  aggregateBusiness: boolean; // 集計行かどうか
  receiptForecastMemo?: string; // 受領用内示のメモ項目（内示ファイルから読み取る想定）
  excelInfos?: IExcelInfo[]; // エラーログ等の為の追加情報
};

/**
 *
 */
export type RequiredTranslateBusiness = TranslateExcelBusiness | Partial<TranslateExcelBusinessId>;

/**
 *
 */
type TranslateExcelBusinessId = {
  warehouse: string;
  warehouseNumber: string;
  package: string;
  businessNumber: string;
  supplierName: string;
  supplierNumber: string;
};

// TranslateExcelBusinessのキーを追加したら必ずここも追加させること！！！ そして、プロパティの順序は、型定義の順序を担保すること！！！
export const translateExcelBusinessProperties = ['translateBusinessId', 'aggregateBusiness', 'receiptForecastMemo', 'excelInfos'] as const;

/**
 * 対象の取引情報が有効な転記用取引ID作成に必要なパラメータを有しているか(欠けがないか)を確認する
 * @param business - 取引情報
 */
export function isValidTranslateBusinessId(business: RequiredTranslateBusiness): business is TranslateExcelBusiness {
  return (
    Object.prototype.hasOwnProperty.call(business, 'warehouseNumber') &&
    Object.prototype.hasOwnProperty.call(business, 'package') &&
    Object.prototype.hasOwnProperty.call(business, 'businessNumber') &&
    Object.prototype.hasOwnProperty.call(business, 'supplierNumber')
  );
}

/**
 * 対象の取引情報が有効な取引であるかどうかを確認する
 * 転記では、warehouseNumber, package, businessNumber, supplierNumberが必須で空文字でないことを保証する
 * @param business - 取引情報
 */
export function isValidTranslateBusiness(business: RequiredTranslateBusiness | IBusiness) {
  return (
    (business.warehouseNumber ?? '') != '' &&
    (business.package ?? '') != '' &&
    (business.businessNumber ?? '') != '' &&
    (business.supplierNumber ?? '') != ''
  );
}

/**
 * エクセルデータ一行をkey-valueに変換した型
 */
export type ExcelRow = {
  [key: string]: {
    value: string | number | undefined;
    address: string | number | undefined;
    fileName: string | number | undefined;
    sheetName: string | number | undefined;
  };
};

/**
 * エクセルデータ(output)一行をkey-valueに変換した型
 */

/**
 *
 */
export type ExcelOutputRow = {
  [key: string]: {
    value: string | number | undefined; // 転記される直接の値
    address: string | number | undefined; // 転記されるExcelのアドレス
    prevAddress: string | number | undefined; // 転記される前のExcelのアドレス
  };
};

/**
 * Excelに転記する時の型
 * 7/24追加項目、isDuplicatedBusinessは取引重複に該当しているデータかを転記時に判別する必要があるため追加した
 */
export type ExcelOutputResult = {
  [key: string]: {
    value: string | number | undefined; // 転記される直接の値
    prevValue: string | number | undefined; // 転記前に存在した値
    address?: string | number | undefined; // 転記されるExcelのアドレス
    // ここ以下は変更履歴作成に使用するために追加
    columnName: string | undefined; // 転記される列名
    transactionInfo: string[] | undefined; // 転記される取引の取引情報(businessId生成に使用する情報)
    isDuplicatedBusiness: boolean;
  };
};

/**
 * Excel転記の入力エクセルの種類
 */
export const InputExcelType = {
  announce: '内示',
  announceDaily: '日別内示',
  salesResults: '販売実績',
  arrival: '入荷',
  inventory: '在庫',
  order: '発注取込',
} as const;
/**
 * Excel転記の入力エクセルの種類
 */
export type InputExcelType = (typeof InputExcelType)[keyof typeof InputExcelType];

/**
 * Excelに転記する時のエラー　一覧
 * 転記先が存在しない キーが転記先に存在しない場合
 * InappropriateType　転記される文字に関する型エラー (数字が入るべきところで文字が入ってしまっている等） ← TODO: これ別の意味で使われている気がする
 * InputExcelInCorrectType　入力されたエクセルの型エラー
 * OutputExcelInCorrectType　出力されるエクセルの型エラー
 */
export type ExcelTranslateErrorCode =
  | '転記先が存在しない'
  | '取引情報が重複している'
  | 'InappropriateType'
  | 'InputExcelInCorrectType'
  | 'OutputExcelInCorrectType'
  | 'OutputExcelDifferenceCodeAndNameType'
  | 'FailedInputExcel';

/**
 * Excelに転記する時のエラー　一覧
 * 転記先が存在しない キーが転記先に存在しない場合
 * InappropriateType　転記される文字に関する型エラー (数字が入るべきところで文字が入ってしまっている等）
 */
export type ExcelTranslateError = {
  key: string;
  errCode: ExcelTranslateErrorCode;
  // 転記される前のファイル名 (inputFileName: 入力ファイル、registerFileName: 登録済みファイル)
  errorFileName: {
    inputFileName: string;
    registerFileName?: string;
  };
  preCellNumber: string; // 転記される前のセル番号
  errorSheetName?: {
    inputSheetName: string;
    registerSheetName?: string;
  }; // 転記されるシート名
  errorCellNumber: {
    inputCellNumber: string;
    registerCellNumber?: string;
  }; // 転記されるセル番号
  value?: IBusiness; // 転記される値
  errorString?: string; // エラー内容
};

/**
 * 登録済みのファイル情報(ファイル名、シート名、セル番号)
 * 取引重複時など、読み取り済みのファイル情報を保持するためのもの
 *
 */
export type ExcelRegisteredFileInfo = {
  registeredFileName: string;
  registeredSheetName: string;
  registeredCellNumber: string;
};

/**
 * splitExcelDataに当て込むため用の型
 * TranslateExcelBusiness[]が参照型変数のため、直接値を変更するため、返り値はvoid
 * @param business - 転記する取引情報
 * @param excelData - 転記元のExcelデータ
 * @param fileReadErrors - エラー情報を格納するオブジェクト（エラーがあれば追加）
 * @param useValidate - バリデーションを行うかどうか(ログ出力時はvalidate不要な為)
 */
export type AnnounceExcelMapper = (
  business: TranslateExcelBusiness,
  excelData: ExcelRow,
  fileReadErrors: ExcelTranslateError[],
  useValidate: boolean,
  receivedYearMonth?: IYearMonth
) => void;

/**
 * splitExcelDataに当て込むため用の型
 * TranslateExcelBusiness[]が参照型変数のため、直接値を変更するため、返り値はvoid
 * @param business - 転記する取引情報
 * @param excelData - 転記元のExcelデータ
 * @param inputExcelInfo - エクセルの読み替え情報
 * @param fileReadErrors - エラー情報を格納するオブジェクト（エラーがあれば追加）
 * @param useValidate - バリデーションを行うかどうか(ログ出力時はvalidate不要な為)
 */
export type newExcelMapper = (
  business: TranslateExcelBusiness,
  excelData: ExcelRow,
  inputExcelInfo: RegisterExcelInfo,
  fileReadErrors: ExcelTranslateError[],
  useValidate: boolean
) => void;

/**
 * IBusinessの拡張（影響を考慮してここで今はやっているが最終的には移動する）
 * エラーログ等の為の追加情報
 */
export interface IExcelInfo {
  cellNumber: string | number | undefined; // Excelのアドレス
  sheetNumber?: string | number | undefined; // シート名(在庫表のみを想定)
  fileName: string; // ファイル名
  rowObjectInfo: TranslateExcelBusiness; // 出力するためにオブジェクト本体を持っておく（重複を許容するため、キーが重複する可能性は全然あるので、、、）
}
/**
 * parseExcelObjectsで色々やるためのオプション
 *
 */
export type ExcelParseOption = {
  separateYmd: {
    startCell: string;
    separateCount: number;
  };
  fileName: string;
  sheetName: string;
  ignoreRow: string[]; // 読み込みたくない行がある場合
};
/**
 * resetAll()の型定義
 * vue3への移行とともに、typeScriptを4.5.5へバージョンアップした際に発生したes-lintエラーへの対処
 * function = (receiveFunc: Function) => {...}
 * のような処理でFunction型として定義するのは、受け取る関数の型安全性が担保されないので推奨されていない
 * どのような引数を受け取り、どのような値を返すのか定義する必要がある
 *
 */
export type ResetFunction = () => void;

/**
 * 同一取引重複があった際にアラートを表示する関数の型定義
 * showMultipleSameTradeAlert()
 */
export type MultipleSameTradeAlertFunction = () => void;

/**
 * 処理の進捗状況を更新する関数の型定義
 */
export type ProgressUpdateFunction = (progressTaskName: string, addValue: number) => void;

/**
 * 内示エクセルに入力されている情報の年月を持つ
 * エラーログ出力の際に、各項目に年月情報を加えたかったため作成
 * 販売実績、入荷、在庫エクセルについては最新月ではなく、現在は最新の1以上の数量が入った情報に対してなのでコメントアウト
 */
export type ExcelYearMonth = {
  customerAnnounce: string[];
};

// なんか別の管理方法したい（今のところこいつはFetchExcelModalでしか使ってないからimportとかはしてない）
// 一応、FetchExcelModalと同じ定義
/**
 *
 */
export interface IOutputFileInfo {
  [key: string]: {
    id: number;
    label: string;
    active: boolean;
    isDisabled: boolean;
  };
}

/**
 *
 */
export interface IOutputExcelInfoTest {
  label: string;
  data: ExcelOutputResult;
}

/**
 * エクセルデータ一行をkey-valueに変換した型
 */

// export type ExcelRow = { [key: string]: string | number | undefined };

/**
 * splitExcelDataに当て込むため用の型
 * TranslateExcelBusiness[]が参照型変数のため、直接値を変更するため、返り値はvoid
 */
// export type ExcelMapper = (business: TranslateExcelBusiness[], excelData: ExcelRow[]) => void;

/**
 * エクセルの転記で取り扱うキーの集合
 */
export type TranslateKeyName = '内示' | '日別内示' | '販売実績' | '入荷' | '在庫' | '発注取込';

/**
 * 出力するエクセルの種類
 * 重複取引エラーのみ扱いが特別(?)なためこんな感じになっている
 */
export type OutputFileType = '在庫表' | '仕入先内示';

/**
 * 取り扱うエクセルの種類
 */
export type ExcelFileType = TranslateKeyName | OutputFileType;

/**
 * 出力されるcsvファイルの種類
 */
export type OutputCsvType = '変更履歴' | '未転記取引まとめ';

/**
 *
 */
export type OutputFileContents = {
  [contentName: string]: OutputFileContent;
};

/**
 * エクセル転記の結果を格納する型（ここで扱うエラーは特別なエラーで、処理をストップさせないエラーなので別で切り出している）
 * 0: 転記された取引情報(単体テストで使う用)
 * 1: 出力されるBlob
 * 2: 変更履歴
 * 3: 未転記取引情報のエラー
 * 4: 重複取引（入力ファイルの）に関するエラー
 * 5: 未転記取引情報
 */
export type OutputFileContent = [IOutputExcelInfoTest[], Blob, string[][], string[][], string[][], IUnmanagedBusiness[]];

/**
 * 仕入先内示の集計情報をまとめる
 * 現状仕入先内示のみだが、他でも使うようなことがあったら変数名を変更する
 */
export type SupplierForecastAggregateInfo = {
  statistics: number; // 統計
  subTotal: number; // 小計
  totalAmount: number; // 合計
};

/**
 * 更新を行う情報の種類をまとめた型
 * AddPastAchievementInfoDataByTypeのために用意している
 */
export type AddPastAchievementInfoType = '内示' | '実績';

/**
 * AddPastAchievementInfoTypeの型をもとに、更新を行うデータの型の定義を行う
 */
export type AddPastAchievementInfoDataByType<T extends AddPastAchievementInfoType> = T extends '内示' ? IAnnounce : ISalesResult;

/**
 * 在庫エクセルの入力確認
 * 在庫エクセルが入力されたかの結果と入力在庫エクセルの最新年月情報を持たせる
 */
export type ImportInventoryExcelResult = {
  latestInventoryYearMonth: IYearMonth;
  isImport: boolean;
};

/**
 * 内示エクセルの入力確認
 * 内示エクセルが入力されたかの結果と内示受領月情報を持たせる
 * @property manageInitiatedForecastDate - ファイル名をキーとして、IYearMonthの受領月情報を持ったobject
 * @property isImport - 内示がimportされたかどうか
 */
export type ImportAnnounceExcelResult = {
  manageInitiatedForecastDate: Record<string, IYearMonth>;
  isImport: boolean;
};

/**
 * 日別内示エクセルの入力確認
 * 日別内示エクセルが入力されたかの結果と内示受領月情報を持たせる
 * @property manageInitiatedForecastDate - ファイル名をキーとして、IYearMonthの受領月情報を持ったobject
 * @property isImport - 日別内示がimportされたかどうか
 */
export type ImportAnnounceDailyExcelResult = {
  manageInitiatedForecastDate: Record<string, IYearMonth>;
  isImport: boolean;
};

/**
 * エクセルのCDとNameの差異をチェックするためのオブジェクト
 */
export type DifferenceConfirmationCodeAndName = {
  [key: string]: {
    [key: string]: {
      name: string;
      cellNumber: string | number;
    };
  };
};
